/**
 * §9.2 — typed API client for helix-api.
 *
 * Behavior:
 *  - base URL: VITE_API_URL (required in production builds — fail fast if absent);
 *    in dev, `/api` proxies to wrangler on :8787 when VITE_API_URL is unset
 *  - Bearer access token, transparent one-shot refresh on 401
 *  - SSE reader for /v1/chat streams
 *  - OFFLINE DEMO MODE is an explicit choice, never a silent fallback: it engages
 *    ONLY via VITE_DEMO_MODE=true, or in dev with no VITE_API_URL configured.
 *    Network failures in live mode surface as real errors in the UI.
 */

export type OAuthProvider = 'github' | 'google';
export interface User {
  id: string;
  email: string;
  plan: string;
  hasPassword?: boolean;
  authProviders?: OAuthProvider[];
  isAdmin?: boolean;
}
export interface GeneratedAppUser { id: string; name: string; email: string; emailVerified: boolean }
export type GeneratedAppAuthAction =
  | 'register'
  | 'login'
  | 'logout'
  | 'me'
  | 'request-email-verification'
  | 'verify-email'
  | 'forgot-password'
  | 'reset-password';
export type GeneratedAppAuthResult = {
  user: GeneratedAppUser | null;
  verificationRequired?: boolean;
  verificationSent?: boolean;
  verificationDelivery?: 'sent' | 'cooldown' | 'rate_limited' | 'quota_reached' | 'unavailable';
  verificationExpiresInSeconds?: number;
  verificationResendAfterSeconds?: number;
  resetExpiresInSeconds?: number;
  message?: string;
  accepted?: boolean;
  passwordReset?: boolean;
};
export type GeneratedAppOAuthStart = { url: string; channel: string };
export interface SignupPending {
  verificationRequired: true;
  email: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}
export type AdminPlanChoice = 'free' | 'pro' | 'max' | 'business';
/** A row in the owner-only admin accounts panel (GET /v1/admin/users). */
export interface AdminUser {
  id: string;
  email: string;
  plan: string;
  created_at: string;
  projects: number;       // live (non-deleted) projects
  projects_all: number;   // incl. soft-deleted
  builds: number;         // billed runs
  cost_usd: number;
  last_active: string | null;
  last_seen_at: string | null;
  online: boolean;        // received a visible-tab heartbeat in the last 90 seconds
  active: boolean;        // rolling-deploy compatibility alias for online
  is_self: boolean;       // the signed-in admin's own row
  is_admin: boolean;
}
export interface Project { id: string; name: string; slug: string; template: string; created_at: string }
export type ProjectEnvironment = 'preview' | 'production';
export type ProjectEnvironmentKind = 'public' | 'secret';
export interface ProjectEnvironmentVariable {
  environment: ProjectEnvironment;
  name: string;
  kind: ProjectEnvironmentKind;
  value?: string;
  configured: boolean;
  updated_at?: string;
}
export interface CommerceCatalogProduct {
  id: string;
  name: string;
  description?: string;
  currency: string;
  unitAmount: number | null;
  suggestedUnitAmount?: number;
  mode: 'payment';
  draft: boolean;
  fulfillment: 'physical' | 'digital' | 'service';
  shippingCountries?: string[];
  stripeSync?: CommerceProductSyncStatus;
}
export interface CommerceProductSyncStatus {
  productId: string;
  status: 'draft' | 'not_connected' | 'ready_to_sync' | 'syncing' | 'synced' | 'failed' | 'outdated';
  livemode?: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  stripeDashboardUrl?: string;
  syncedAt?: string;
  error?: string;
}
export interface CommerceStatus {
  configured: boolean;
  sandboxConfigured: boolean;
  liveConfigured: boolean;
  webhookConfigured: boolean;
  connected: boolean;
  accountId?: string;
  livemode?: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  authorizationStatus: 'authorized' | 'deauthorized';
  stripeRestrictionStatus: 'unknown' | 'clear' | 'restricted';
  stripeRestrictionReason?: string;
  riskStatus: 'unknown' | 'clear' | 'paused';
  riskReason?: string;
  riskCheckedAt?: string;
  publishedCheckoutStatus: 'active' | 'paused';
  publishedCheckoutReason?: string;
  policyAcceptedAt?: string;
  platformStatus: 'active' | 'suspended';
  platformReason?: string;
  catalog: {
    valid: boolean;
    products: number;
    draftProducts: number;
    syncedProducts: number;
    productsNeedAction: number;
    items?: CommerceCatalogProduct[];
    error?: string | null;
  };
  ready: boolean;
}
export interface CommerceOrder {
  id: string;
  stripe_payment_intent_id?: string | null;
  livemode: number;
  status: 'creating' | 'open' | 'processing' | 'paid' | 'failed' | 'expired' | 'partially_refunded' | 'refunded';
  payment_status: string;
  dispute_status?: string | null;
  fulfillment_status: 'unfulfilled' | 'fulfilled' | 'not_required';
  currency: string;
  amount_total: number;
  amount_refunded: number;
  items: Array<{ product_id: string; product_name: string; unit_amount: number; quantity: number }>;
  customer_email?: string | null;
  checkout_created_at: string;
  paid_at?: string | null;
  fulfilled_at?: string | null;
  updated_at: string;
}
export interface CommerceTotal {
  currency: string;
  orders: number;
  gross: number;
  refunded: number;
  disputed: number;
}
export interface AdminCommerceProject {
  project_id: string;
  project_name: string;
  owner_email: string;
  stripe_account_id: string;
  livemode: number;
  charges_enabled: number;
  payouts_enabled: number;
  details_submitted: number;
  authorization_status: 'authorized' | 'deauthorized';
  stripe_restriction_status: 'unknown' | 'clear' | 'restricted';
  stripe_restriction_reason?: string | null;
  risk_status: 'unknown' | 'clear' | 'paused';
  risk_reason?: string | null;
  published_checkout_status: 'active' | 'paused';
  published_checkout_reason?: string | null;
  platform_status: 'active' | 'suspended';
  platform_reason?: string | null;
  connected_at: string;
  updated_at: string;
  orders: number;
  paid_orders: number;
  disputes: number;
}
export interface CustomDomain {
  hostname: string;
  url: string;
  status: string;
  sslStatus: string;
  active: boolean;
  cnameTarget: string;
  ownershipVerification: { type: 'TXT'; name: string; value: string } | null;
  validationErrors: string[];
  updatedAt: string;
}
export interface CustomDomainStatus {
  available: boolean;
  configured: boolean;
  domain: CustomDomain | null;
}
export interface FileMeta { path: string; size: number; updated_at: string }
export interface Snapshot { run_id: string; label: string; file_count: number; created_at: string }
export interface Usage { period: string; tokens_in: number; tokens_out: number; billed_usd: number }
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  run_id: string | null;
  /** Older API versions omit commit metadata; hydration still checks run results. */
  committed?: boolean;
  created_at: string;
}
/** A successful, compiled build (commits are written ONLY on a PROVE pass). The
 *  presence of any commit is the "has been built" signal that ends intake mode. */
export interface Commit { run_id: string; tag: string; summary: string; file_count: number; created_at: string }
/* Intake funnel — mirrors helix-api src/types.ts (NOT an SSE event). */
export type IntakeQuestionType = 'pills' | 'radio' | 'text';
export interface IntakeOption { label: string; value: string }
export interface IntakeQuestion { id: string; label: string; type: IntakeQuestionType; options?: IntakeOption[]; required?: boolean }
export interface IntakeQuestionnaire {
  title: string;
  questions: IntakeQuestion[];
  commerce?: boolean;
  revision?: string;
  /** Project-owned source prompt, returned so a fresh tab can submit restored choices. */
  prompt?: string;
  /** Clear conversation turns bypass first-build intake and never force a build. */
  mode?: 'build' | 'discuss';
}
export interface RequirementAnswer { id: string; label: string; value: string }
export type PreprojectResult =
  | { mode: 'discuss'; reply: string; conversationToken: string }
  | { mode: 'build'; prompt: string; titlePrompt: string };
type PreprojectStreamEvent =
  | { type: 'status'; status: 'thinking' }
  | { type: 'token'; text: string }
  | { type: 'complete'; reply: string; conversationToken: string }
  | { type: 'build'; prompt: string; titlePrompt: string }
  | { type: 'error'; message: string };
/** A live-preview element selection (Select-to-Scope) — mirrors helix-api
 *  src/types.ts. Rides the /v1/chat body so a build edits ONLY this element's
 *  file. `html` is a human hint only; the backend derives the edit anchor from
 *  `path` + `line`/`col`. */
export interface SelectionScope {
  path: string;
  line?: number;
  col?: number;
  tag?: string;
  /** JSX identifier at the source marker when a component renders a different
   * DOM tag (for example, a router Link rendering an anchor). */
  sourceTag?: string;
  className?: string;
  text?: string;
  html?: string;
  mapIndex?: number;
  commerceProductId?: string;
  commerceField?: 'price';
}

export interface ExperienceProofSummary {
  status: 'passed' | 'warning' | 'skipped';
  desktop: boolean;
  mobile: boolean;
  linksChecked: number;
  controlsChecked: number;
  warnings: string[];
}

import { turnstileHeader } from './turnstile';
import { createRefreshCoordinator, createStorageLeaseLock } from './refresh-coordinator';
export { isContextLimitError } from './build-errors';

export type SseEvent =
  | { type: 'phase'; name: string }
  | { type: 'token'; text: string }
  | { type: 'file'; path: string; action: 'write' | 'patch' }
  | { type: 'experience'; runId: string; proof: ExperienceProofSummary }
  | { type: 'usage'; tokens_in: number; tokens_out: number; billed: boolean; credits_used?: number }
  | { type: 'done'; runId: string; images?: string[]; experience?: ExperienceProofSummary }
  | { type: 'error'; message: string; code?: 'context_limit' };

/** Frozen stream contract (§10.2): exactly these event names. Frames with
 *  unknown types are dropped at the boundary so future backend additions
 *  can never reach UI code as miscast objects. */
const SSE_TYPES = new Set(['phase', 'token', 'file', 'experience', 'usage', 'done', 'error']);
function isSseEvent(v: unknown): v is SseEvent {
  return typeof v === 'object' && v !== null
    && SSE_TYPES.has((v as { type?: unknown }).type as string);
}

if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
  throw new Error('VITE_API_URL is not set — production builds must point at the Piixel API');
}

const BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');
const ACCESS_KEY = 'helix_access';
const REFRESH_KEY = 'helix_refresh';

/** Demo mode is opt-in only: debug env var, or a dev server with no backend
 *  configured. It is decided once at module load and never flips at runtime. */
const DEMO_MODE: boolean =
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  (import.meta.env.DEV && !import.meta.env.VITE_API_URL);

export function isDemoMode(): boolean { return DEMO_MODE; }

export function oauthStartUrl(provider: OAuthProvider, returnTo: string, failureTo: string): string {
  const query = new URLSearchParams({ returnTo, failureTo });
  return `${BASE}/v1/auth/oauth/${provider}/start?${query.toString()}`;
}

const CONNECTIVITY_ERROR = 'Could not reach Piixel — check your connection.';
const sleep = (ms: number, signal?: AbortSignal): Promise<void> => new Promise((resolve) => {
  if (signal?.aborted) { resolve(); return; }
  const timer = setTimeout(done, ms);
  function done(): void {
    clearTimeout(timer);
    signal?.removeEventListener('abort', done);
    resolve();
  }
  signal?.addEventListener('abort', done, { once: true });
});

export function getAccess(): string | null {
  // §9.6 prerender (SSR): no localStorage → render as signed out; hydration corrects.
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ACCESS_KEY);
}
export function isAuthed(): boolean { return !!getAccess(); }
/** True when the access token is absent or within `skewMs` of expiry — lets us
 *  refresh BEFORE a WebSocket handshake (which, unlike fetch, can't 401-and-retry). */
function accessExpiresSoon(skewMs = 30_000): boolean {
  const t = getAccess();
  if (!t) return true;
  try {
    const part = (t.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const exp = (JSON.parse(atob(part)) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 - Date.now() < skewMs : false;
  } catch { return false; /* opaque token → let the handshake/SSE-fallback handle it */ }
}
function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

interface Envelope<T> { ok: boolean; data?: T; error?: { code: string; message: string } }

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  if (DEMO_MODE) return demoRequest<T>(path, init);
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    console.error('[helix-api] network failure on', path, err);
    throw new Error(CONNECTIVITY_ERROR);
  }
  if (res.status === 401 && !retried && requestRefresh) {
    const ok = await tryRefresh(requestRefresh);
    if (ok) return request<T>(path, init, true);
  }
  const body = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (body === null) {
    console.error('[helix-api] non-JSON reply', res.status, 'on', path);
    throw new Error(`Piixel returned an unexpected response (${res.status})`);
  }
  if (!body.ok || body.data === undefined) {
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return body.data;
}

async function requestBlob(path: string, retried = false): Promise<Blob> {
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: requestAccess ? { authorization: `Bearer ${requestAccess}` } : {},
    });
  } catch (err) {
    console.error('[helix-api] network failure on', path, err);
    throw new Error(CONNECTIVITY_ERROR);
  }
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return requestBlob(path, true);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as Envelope<unknown> | null;
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return res.blob();
}

/** Authenticated JSON document endpoint. Experience-proof reports are stored
 * verbatim in R2 and intentionally do not use the normal API envelope. */
async function requestJsonDocument<T>(path: string, retried = false): Promise<T> {
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: requestAccess ? { authorization: `Bearer ${requestAccess}` } : {},
    });
  } catch (err) {
    console.error('[helix-api] network failure on', path, err);
    throw new Error(CONNECTIVITY_ERROR);
  }
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return requestJsonDocument<T>(path, true);
  }
  const body = (await res.json().catch(() => null)) as T | Envelope<unknown> | null;
  if (!res.ok) {
    const envelope = body as Envelope<unknown> | null;
    throw new Error(envelope?.error?.message ?? `Request failed (${res.status})`);
  }
  if (body === null) throw new Error(`Piixel returned an unexpected response (${res.status})`);
  return body as T;
}

/** Live pre-project dialogue. This uses an additive endpoint so stable clients
 * can keep consuming the original JSON route during a gradual rollout. */
export async function streamPreproject(
  message: string,
  conversationToken: string | undefined,
  onToken: (text: string) => void,
  retried = false,
  turnId = crypto.randomUUID(),
): Promise<PreprojectResult> {
  if (DEMO_MODE) {
    const result = await request<PreprojectResult>('/v1/projects/preproject', {
      method: 'POST', body: JSON.stringify({ message, turnId, ...(conversationToken ? { conversationToken } : {}) }),
    });
    if (result.mode === 'discuss') onToken(result.reply);
    return result;
  }

  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}/v1/projects/preproject/stream`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
        ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}),
      },
      body: JSON.stringify({ message, turnId, ...(conversationToken ? { conversationToken } : {}) }),
    });
  } catch (error) {
    console.error('[helix-api] network failure on /v1/projects/preproject/stream', error);
    throw new Error(CONNECTIVITY_ERROR);
  }

  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return streamPreproject(message, conversationToken, onToken, true, turnId);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as Envelope<unknown> | null;
    throw new Error(body?.error?.message ?? `Project planning failed (${res.status})`);
  }
  if (!res.body || !res.headers.get('content-type')?.includes('text/event-stream')) {
    throw new Error('Piixel returned an unexpected planning response.');
  }

  let terminal: PreprojectResult | undefined;
  const accept = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    const event = value as Partial<PreprojectStreamEvent>;
    if (event.type === 'token' && typeof event.text === 'string') onToken(event.text);
    else if (event.type === 'complete' && typeof event.reply === 'string' && typeof event.conversationToken === 'string') {
      terminal = { mode: 'discuss', reply: event.reply, conversationToken: event.conversationToken };
    } else if (event.type === 'build' && typeof event.prompt === 'string' && typeof event.titlePrompt === 'string') {
      terminal = { mode: 'build', prompt: event.prompt, titlePrompt: event.titlePrompt };
    } else if (event.type === 'error') {
      throw new Error(typeof event.message === 'string' ? event.message : 'Project planning failed.');
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const consumeLines = (final = false): void => {
    const lines = buffer.split(/\r?\n/);
    buffer = final ? '' : (lines.pop() ?? '');
    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      try { accept(JSON.parse(line.slice(5).trim())); } catch (error) {
        if (error instanceof SyntaxError) continue;
        throw error;
      }
    }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    consumeLines();
  }
  buffer += decoder.decode();
  if (buffer) { buffer += '\n'; consumeLines(true); }
  if (!terminal) throw new Error('The planning response ended before it was complete.');
  return terminal;
}

// Refresh tokens rotate and their family is revoked on reuse. The coordinator
// coalesces calls in this tab; Web Locks (or the storage lease fallback) extends
// the guarantee across all tabs on the same Piixel origin.
const storageRefreshLock = typeof localStorage !== 'undefined'
  ? createStorageLeaseLock(localStorage)
  : async <T>(task: () => Promise<T>): Promise<T> => task();
const coordinateRefresh = createRefreshCoordinator({
  currentRefresh: () => localStorage.getItem(REFRESH_KEY),
  withCrossTabLock: async (task) => {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request('piixel-auth-refresh', { mode: 'exclusive' }, task);
    }
    return storageRefreshLock(task);
  },
  rotate: async (refresh) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12_000);
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh }),
        signal: controller.signal,
      });
      const body = (await res.json().catch(() => null)) as Envelope<{ access: string; refresh: string }> | null;
      if (res.ok && body?.ok && body.data) {
        setTokens(body.data.access, body.data.refresh);
        return 'refreshed';
      }
      // Only an explicit client-side rejection proves the rotating token is no
      // longer usable. Rate limits, upstream faults and malformed responses are
      // transient and must never sign every tab out.
      if (res.status === 400 || res.status === 401 || res.status === 403) return 'invalid';
      throw new Error(body?.error?.message ?? 'Piixel could not refresh the session. Please try again.');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Piixel session refresh timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  },
  invalidate: () => clearTokens(),
});

function tryRefresh(expectedRefresh: string | null): Promise<boolean> {
  return coordinateRefresh(expectedRefresh);
}

/* ── Public API surface (mirrors §10.2) ─────────────────────────────────── */

export const api = {
  async signup(email: string, password: string): Promise<SignupPending> {
    return request<SignupPending>(
      '/v1/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        headers: await turnstileHeader('signup'),
      });
  },
  async verifySignup(email: string, code: string): Promise<User> {
    const d = await request<{ user: User; access: string; refresh: string }>(
      '/v1/auth/signup/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code }),
        headers: await turnstileHeader('signup'),
      });
    setTokens(d.access, d.refresh);
    return d.user;
  },
  async login(email: string, password: string): Promise<User> {
    const d = await request<{ user: User; access: string; refresh: string }>(
      '/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        headers: await turnstileHeader('login'),
      });
    setTokens(d.access, d.refresh);
    return d.user;
  },
  async consumeOAuth(code: string): Promise<
    | { kind: 'session'; user: User; provider: OAuthProvider; returnTo: string }
    | { kind: 'account_deleted'; returnTo: string }
  > {
    if (DEMO_MODE) {
      await api.login('demo@piixel.ai', 'demo-password');
      return { kind: 'session', user: demoDb.user, provider: 'github', returnTo: '/dashboard' };
    }
    const d = await request<
      | { kind: 'session'; user: User; provider: OAuthProvider; returnTo: string; access: string; refresh: string }
      | { kind: 'account_deleted'; returnTo: string }
    >('/v1/auth/oauth/consume', { method: 'POST', body: JSON.stringify({ code }) });
    if (d.kind === 'session') setTokens(d.access, d.refresh);
    else clearTokens();
    return d;
  },
  oauthReauth: (provider: OAuthProvider) =>
    request<{ url: string }>(`/v1/auth/oauth/${provider}/reauth`, { method: 'POST', body: '{}' }),
  me: () => request<{ user: User }>('/v1/auth/me'),
  presence: () => request<{ online: boolean }>('/v1/auth/presence', { method: 'POST', body: '{}' }),
  /** Self-service account erasure (GDPR Art. 17) — requires the current password. */
  deleteAccount: (password: string) =>
    request<{ deleted: boolean }>('/v1/auth/me', { method: 'DELETE', body: JSON.stringify({ password }) }),
  /** Change password (Settings). Other devices sign out; this one gets fresh tokens. */
  async changePassword(current: string, next: string): Promise<void> {
    const d = await request<{ changed: boolean; access: string; refresh: string }>(
      '/v1/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) });
    setTokens(d.access, d.refresh);
  },
  /* ── GitHub connection (Settings) ── */
  githubConnection: () => request<{ connected: boolean; login?: string }>('/v1/github/connection'),
  githubDisconnect: () => request<{ disconnected: boolean }>('/v1/github/connection', { method: 'DELETE' }),
  listProjects: () => request<{ projects: Project[] }>('/v1/projects'),
  getProject: (id: string) => request<{ project: Project }>(`/v1/projects/${id}`),
  projectEnvironment: (id: string) =>
    request<{ variables: ProjectEnvironmentVariable[] }>(`/v1/projects/${id}/environment`),
  saveProjectEnvironment: (
    id: string,
    environment: ProjectEnvironment,
    name: string,
    kind: ProjectEnvironmentKind,
    value: string,
  ) => request<{ variable: ProjectEnvironmentVariable }>(
    `/v1/projects/${id}/environment/${environment}/${encodeURIComponent(name)}`,
    { method: 'PUT', body: JSON.stringify({ kind, value }) },
  ),
  deleteProjectEnvironment: (id: string, environment: ProjectEnvironment, name: string) =>
    request<{ deleted: boolean }>(
      `/v1/projects/${id}/environment/${environment}/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),
  /** Real generated-app auth. The server stores users/sessions in APP_DB; the
   * browser receives only a project-specific HttpOnly preview cookie. */
  appAuth: (
    id: string,
    action: GeneratedAppAuthAction,
    input?: { name?: string; email?: string; password?: string; code?: string },
  ) => request<GeneratedAppAuthResult>(`/v1/projects/${id}/app-auth/${action}`, {
    method: action === 'me' ? 'GET' : 'POST',
    credentials: 'include',
    ...(action === 'me' ? {} : { body: JSON.stringify(input ?? {}) }),
  }),
  /** Starts project-scoped Google OAuth without exposing provider credentials to
   * the generated app or its opaque-origin preview frame. */
  appAuthOAuthStart: (id: string, channel: string) =>
    request<GeneratedAppOAuthStart>(`/v1/projects/${id}/app-auth/oauth/google/start`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ channel }),
    }),
  commerceStatus: (id: string) => request<CommerceStatus>(`/v1/commerce/projects/${id}/status`),
  commerceProductPrice: (
    id: string,
    productId: string,
    input: { unitAmount: number; currency?: string; shippingCountries?: string[]; editId: string },
  ) => request<{
    editId: string;
    undoId: string;
    product: CommerceCatalogProduct;
    shippingRequired: boolean;
    file: { path: string; content: string };
    files: Array<{ path: string; content: string }>;
    catalog: { valid: true; products: CommerceCatalogProduct[]; draftProducts: number };
    replayed: boolean;
    stripeSync: CommerceProductSyncStatus;
  }>(`/v1/commerce/projects/${id}/catalog/products/${encodeURIComponent(productId)}/price`, {
    method: 'PUT', body: JSON.stringify(input),
  }),
  commerceProductSync: (id: string, productId: string) =>
    request<{ status: CommerceProductSyncStatus }>(
      `/v1/commerce/projects/${id}/catalog/products/${encodeURIComponent(productId)}/sync`,
      { method: 'POST' },
    ),
  commerceReconcileCatalog: (id: string) => request<{
    changed: boolean;
    files: Array<{ path: string; content: string }>;
  }>(`/v1/commerce/projects/${id}/catalog/reconcile`, { method: 'POST' }),
  commerceConnect: (id: string, acceptRestrictedBusinessPolicy: boolean, mode: 'sandbox' | 'live') =>
    request<{ url: string }>(`/v1/commerce/projects/${id}/connect`, {
      method: 'POST', body: JSON.stringify({ acceptRestrictedBusinessPolicy, mode }),
    }),
  commerceDisconnect: (id: string) =>
    request<{ disconnected: boolean }>(`/v1/commerce/projects/${id}/connection`, { method: 'DELETE' }),
  commerceOrders: (id: string) =>
    request<{ orders: CommerceOrder[]; totals: CommerceTotal[]; nextBefore?: string }>(
      `/v1/commerce/projects/${id}/orders`,
    ),
  commerceFulfillment: (id: string, orderId: string, status: 'fulfilled' | 'unfulfilled') =>
    request<{ id: string; fulfillmentStatus: 'fulfilled' | 'unfulfilled' }>(
      `/v1/commerce/projects/${id}/orders/${encodeURIComponent(orderId)}/fulfillment`,
      { method: 'PUT', body: JSON.stringify({ status }) },
    ),
  commerceCheckout: (id: string, input: {
    productId?: string;
    quantity?: number;
    items?: Array<{ productId: string; quantity?: number }>;
    requestId: string;
  }) =>
    request<{ url: string }>(`/v1/commerce/projects/${id}/checkout`, {
      method: 'POST', body: JSON.stringify({ ...input, returnPath: `/projects/${id}` }),
    }),
  adminCommerceProjects: () =>
    request<{ projects: AdminCommerceProject[] }>('/v1/admin/commerce/projects'),
  adminCommerceStatus: (id: string, status: 'active' | 'suspended', reason?: string) =>
    request<{ projectId: string; stripeAccountId: string; affectedProjects: number; status: 'active' | 'suspended'; reason?: string | null }>(
      `/v1/admin/commerce/projects/${id}/status`,
      { method: 'PUT', body: JSON.stringify({ status, reason }) },
    ),
  /** name → explicit title; prompt → invented title; blank → empty runnable project. */
  createProject: (opts: { name?: string; prompt?: string; initialPrompt?: string; blank?: boolean }) =>
    request<{ project: Project }>('/v1/projects', { method: 'POST', body: JSON.stringify(opts) }),
  /** Real AI planning dialogue before a project exists. A project is created
   * only after the server returns a concrete build intent. */
  preproject: (message: string, conversationToken?: string) =>
    request<PreprojectResult>('/v1/projects/preproject', {
      method: 'POST', body: JSON.stringify({ message, turnId: crypto.randomUUID(), ...(conversationToken ? { conversationToken } : {}) }),
    }),
  preprojectStream: (message: string, conversationToken: string | undefined, onToken: (text: string) => void) =>
    streamPreproject(message, conversationToken, onToken),
  renameProject: (id: string, name: string) =>
    request<{ project: Project }>(`/v1/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteProject: (id: string) =>
    request<Record<string, never>>(`/v1/projects/${id}`, { method: 'DELETE' }),
  listFiles: (id: string) => request<{ files: FileMeta[] }>(`/v1/projects/${id}/files`),
  /** Bulk hydrate: metadata + contents in ONE round trip (?content=1). An older
   *  worker ignores the param and returns metadata only — callers must fall
   *  back to per-file fileContent() when `content` is missing. */
  listFilesWithContent: (id: string) =>
    request<{ files: (FileMeta & { content?: string })[] }>(`/v1/projects/${id}/files?content=1`),
  fileContent: (id: string, path: string) =>
    request<{ path: string; content: string }>(`/v1/projects/${id}/files/content?path=${encodeURIComponent(path)}`),
  experienceProofImage: (id: string, runId: string, viewport: 'desktop' | 'mobile') =>
    requestBlob(`/v1/projects/${id}/runs/${encodeURIComponent(runId)}/experience/${viewport}`),
  experienceProof: (id: string, runId: string) =>
    requestJsonDocument<{ summary: ExperienceProofSummary }>(`/v1/projects/${id}/runs/${encodeURIComponent(runId)}/experience`),
  saveThumbnail: (id: string, html: string) =>
    request<{ saved: boolean }>(`/v1/projects/${id}/thumbnail`, { method: 'POST', body: JSON.stringify({ html }) }),
  async thumbnail(id: string): Promise<Blob> {
    const res = await fetch(`${BASE}/v1/projects/${id}/thumbnail`, {
      headers: getAccess() ? { authorization: `Bearer ${getAccess()}` } : {},
    });
    if (!res.ok) throw new Error('Preview not ready');
    return res.blob();
  },
  saveFile: (id: string, path: string, content: string) =>
    request<{ file: FileMeta }>(`/v1/projects/${id}/files`,
      { method: 'PUT', body: JSON.stringify({ path, content }) }),
  listMessages: (id: string) => request<{ messages: ChatMessage[] }>(`/v1/projects/${id}/messages`),
  /** Is a run still executing server-side? Lets a reloaded Builder rejoin it. */
  runStatus: (id: string) => request<{ active: boolean; runId: string | null }>(`/v1/projects/${id}/run-status`),
  /** Durable terminal truth when a WS/SSE connection ended without done/error. */
  runResult: (id: string, runId: string) => request<{
    state: 'running' | 'pending' | 'succeeded' | 'failed'; message?: string;
  }>(`/v1/projects/${id}/run-result?runId=${encodeURIComponent(runId)}`),
  /** User-facing credits (abstracts away $ cost). Planning/small changes use 1,
   * normal component changes 3, and full builds/larger features 10.
   *  `plan` is the user's tier (free|pro|max|team) — drives the badge + upgrade UI. */
  credits: () => request<{ remaining: number; total: number; resetsAt: string; plan?: string }>('/v1/credits'),
  listSnapshots: (id: string) => request<{ snapshots: Snapshot[] }>(`/v1/projects/${id}/snapshots`),
  /** Successful builds — `commits.length > 0` means the project has compiled at least once. */
  commits: (id: string) => request<{ commits: Commit[] }>(`/v1/projects/${id}/commits`),
  /** Model-generated first-build questionnaire. The server durably restores the exact issued choices. */
  intake: (id: string, prompt: string) =>
    request<IntakeQuestionnaire>(`/v1/projects/${id}/intake`, { method: 'POST', body: JSON.stringify({ prompt }) }),
  restore: (id: string, runId: string) =>
    request<{ restored: boolean }>(`/v1/projects/${id}/restore/${runId}`, { method: 'POST' }),
  usage: () => request<Usage>('/v1/usage'),
  /* ── publish: a public, shareable URL for the generated app ── */
  publishStatus: (id: string) =>
    request<{ published: boolean; slug?: string; url?: string; updated_at?: string }>(`/v1/projects/${id}/publish`),
  publishProject: (id: string, html: string, sourceRevision: string) =>
    request<{ published: boolean; slug: string; url: string }>(
      `/v1/projects/${id}/publish`, { method: 'POST', body: JSON.stringify({ html, sourceRevision }) }),
  unpublishProject: (id: string) =>
    request<{ unpublished: boolean }>(`/v1/projects/${id}/publish`, { method: 'DELETE' }),
  customDomainStatus: (id: string, refresh = false) =>
    request<CustomDomainStatus>(`/v1/projects/${id}/domain${refresh ? '?refresh=1' : ''}`),
  connectCustomDomain: (id: string, hostname: string) =>
    request<CustomDomainStatus>(`/v1/projects/${id}/domain`, {
      method: 'POST', body: JSON.stringify({ hostname }),
    }),
  removeCustomDomain: (id: string) =>
    request<{ removed: boolean }>(`/v1/projects/${id}/domain`, { method: 'DELETE' }),
  /* ── owner-only admin (server re-checks the email allowlist on every call) ── */
  adminUsers: () => request<{ users: AdminUser[]; count: number }>('/v1/admin/users'),
  adminSetOwnPlan: (plan: AdminPlanChoice) =>
    request<{ plan: string; selectedPlan: AdminPlanChoice }>('/v1/admin/me/plan', {
      method: 'PUT', body: JSON.stringify({ plan }),
    }),
  adminSetUserPlan: (id: string, plan: AdminPlanChoice, expectedPlan: string) =>
    request<{ userId: string; plan: string; selectedPlan: AdminPlanChoice }>(`/v1/admin/users/${encodeURIComponent(id)}/plan`, {
      method: 'PUT', body: JSON.stringify({ plan, expectedPlan }),
    }),
  adminDeleteUser: (id: string) =>
    request<{ deleted: boolean; email: string; projectsQueued: number; projectsTotal: number }>(
      `/v1/admin/users/${id}`, { method: 'DELETE' }),
  adminPurgeUserProjects: (id: string) =>
    request<{
      operationId: string;
      status: 'queued' | 'running' | 'complete';
      runner: 'queue' | 'pinned_poll';
      queued: number;
      total: number;
      remaining: number;
    }>(`/v1/admin/users/${id}/projects`, { method: 'DELETE' }),
  adminPurgeUserProjectsStatus: (id: string, operationId: string) =>
    request<{
      operationId: string;
      status: 'running' | 'complete';
      remaining: number;
    }>(`/v1/admin/users/${id}/projects/purge-status?operationId=${encodeURIComponent(operationId)}`, {
      method: 'POST',
    }),
  /* ── billing (Stripe Checkout + Portal) ── */
  /** Start a Checkout for a paid plan; returns the hosted Stripe URL to redirect to. */
  billingCheckout: (plan: string) =>
    request<{ url: string }>('/v1/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
  /** Open the Stripe Billing Portal (manage/cancel); returns the hosted URL. */
  billingPortal: () => request<{ url: string }>('/v1/billing/portal', { method: 'POST' }),
};

/* ── Feature 2: deterministic visual style edits (docs/visual-style-editor.md
      in the API repo — THE CONTRACT). Errors are branched on error.code, never
      HTTP status (409 covers both busy and stale_loc), so these bypass the
      code-losing request() helper the same way the zip export does. ───────── */

export type StyleBreakpoint = 'base' | 'tablet' | 'phone';

export type StyleOpWire =
  | { prop: 'width' | 'height'; value: number | 'auto' | 'full' | 'screen' | '1/2' | '1/3' | '2/3' | '1/4' | '3/4'; breakpoint?: StyleBreakpoint }
  | { prop: 'fontSize'; value: number; breakpoint?: StyleBreakpoint }
  | { prop: 'fontWeight'; value: number; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: 'fontFamily'; value: string } // curated font id — never a breakpoint
  | { prop: 'color' | 'backgroundColor'; value: { token: string } | { hex: string }; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: 'textAlign'; value: 'left' | 'center' | 'right'; breakpoint?: StyleBreakpoint }
  | { prop: 'verticalAlign'; value: 'top' | 'middle' | 'bottom'; breakpoint?: StyleBreakpoint }
  | { prop: 'borderRadius'; value: number | 'full'; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: 'shadow'; value: 'none' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl'; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: 'borderWidth'; value: 0 | 1 | 2 | 4 | 8; breakpoint?: StyleBreakpoint }
  | { prop: 'borderColor'; value: { token: string } | { hex: string }; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: 'opacity'; value: number; breakpoint?: StyleBreakpoint; state?: 'hover' }
  | { prop: `padding${'Top' | 'Right' | 'Bottom' | 'Left'}` | `margin${'Top' | 'Right' | 'Bottom' | 'Left'}`; value: number; breakpoint?: StyleBreakpoint }
  | { prop: 'textContent'; value: string };

export interface StyleFont { id: string; label: string; css: string; weights: number[]; import: string | null }

export interface StyleInspectData {
  editable: boolean;
  reason: 'not_react' | 'stale_loc' | 'dynamic_class' | null;
  className: string;
  textLiteral: string | null;
  tokens: Array<{ name: string; hex: string }>;
  fonts: StyleFont[];
  sharedImporters: number;
  inMap: boolean;
  mappedActionRole: 'none' | 'product-item' | 'repeated-template';
  stableCommerceProductId?: string;
}

export interface StyleApplyResult {
  changed?: boolean;
  editId: string;
  undoId: string;
  applied: Array<{ prop: string; breakpoint?: StyleBreakpoint; utility: string }>;
  files: Array<{ path: string; content: string }>;
  loc: { path: string; line: number; col: number };
  tokensCreated: Array<{ name: string; hex: string }>;
  warnings: Array<{ code: string; message: string }>;
  recap: string;
}

export type StyleErrorCode =
  | 'invalid' | 'not_react' | 'stale_loc' | 'dynamic_class' | 'dynamic_text'
  | 'busy' | 'prove_failed' | 'rate_limited' | 'internal' | 'error';

export class StyleApiError extends Error {
  constructor(message: string, readonly code: StyleErrorCode) { super(message); }
}

async function styleRequest<T>(path: string, body: unknown, retried = false): Promise<T> {
  if (DEMO_MODE) throw new StyleApiError("Visual editing isn't available in offline demo mode", 'error');
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}),
    },
    body: JSON.stringify(body),
  }).catch(() => { throw new StyleApiError(CONNECTIVITY_ERROR, 'error'); });
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return styleRequest<T>(path, body, true);
  }
  const env = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (!env?.ok || env.data === undefined) {
    throw new StyleApiError(env?.error?.message ?? `Style edit failed (${res.status})`, (env?.error?.code as StyleErrorCode) ?? 'error');
  }
  return env.data;
}

/** Session-start read: is the element editable, its source className/text,
 *  the app's palette tokens and the curated font table. Lock-free. */
export function styleInspect(projectId: string, scope: SelectionScope): Promise<StyleInspectData> {
  return styleRequest<StyleInspectData>(`/v1/projects/${projectId}/style-edits/inspect`, { scope });
}

/** Flush a batch of semantic ops. Free (no credits). `editId` makes retries
 *  after a lost response idempotent — resend the SAME id verbatim. */
export function styleApply(projectId: string, scope: SelectionScope, ops: StyleOpWire[], editId: string): Promise<StyleApplyResult> {
  return styleRequest<StyleApplyResult>(`/v1/projects/${projectId}/style-edits`, { scope, ops, editId });
}

/** Client-minted idempotency id for one flush. */
export function mintEditId(): string {
  const rand = crypto.getRandomValues(new Uint8Array(9));
  return `sed_${btoa(String.fromCharCode(...rand)).replace(/[+/=]/g, 'x').slice(0, 12)}`;
}

/* ── batch ③: element CRUD (insert curated templates / duplicate / delete).
      Line-splicing edits — instant apply, response carries the new element's
      loc so the Builder re-selects it after the rebuild. ─────────────────── */

export interface ElementTemplateMeta { id: string; label: string; tag: string; icon: string }

export interface ElementActionResult {
  editId: string;
  undoId: string;
  /** False means the requested operation was already true in authoritative
   * source. The editor must not manufacture history or reload for it. */
  changed?: boolean;
  files: Array<{ path: string; content: string }>;
  newLoc: { path: string; line: number; col: number; tag: string; mapIndex?: number; commerceProductId?: string } | null;
  warnings: Array<{ code: string; message: string }>;
  recap: string;
}

export type ElementMovePosition = 'before' | 'after' | 'inside' | 'left' | 'right';

export interface ElementMoveTarget {
  path: string;
  line: number;
  tag: string;
  sourceTag?: string;
  mapIndex?: number;
  commerceProductId?: string;
}

/** One browser-side move contract shared by canvas and Page contents. `loc`
 * plus `mapIndex` identify the rendered instance; path/line/tag remain the
 * authoritative source anchor understood by the element API. */
export interface ElementMoveCommand {
  id: string;
  origin: 'canvas' | 'tree';
  source: SelectionScope & { loc: string };
  target: SelectionScope & { loc: string };
  position: ElementMovePosition;
  sourceWidth?: number;
  preserveSize: boolean;
  optimistic: boolean;
}

export interface ElementActionOptions {
  position?: ElementMovePosition;
  template?: string;
  target?: ElementMoveTarget;
  sourceWidth?: number;
  preserveSize?: boolean;
}

export function elementTemplates(projectId: string): Promise<{ templates: ElementTemplateMeta[] }> {
  if (DEMO_MODE) return Promise.reject(new StyleApiError("Element editing isn't available in offline demo mode", 'error'));
  return request<{ templates: ElementTemplateMeta[] }>(`/v1/projects/${projectId}/elements/templates`);
}

/** Group contiguous sibling elements: wrap in a container, or extract into a
 *  reusable src/components/<Name>.tsx (deterministic for static JSX only). */
export function groupElements(
  projectId: string,
  scopes: SelectionScope[],
  mode: 'wrap' | 'component',
  name: string | undefined,
  editId: string,
): Promise<ElementActionResult> {
  return styleRequest<ElementActionResult>(`/v1/projects/${projectId}/elements`, {
    scope: scopes[0], scopes, action: 'group', mode, ...(name ? { name } : {}), editId,
  });
}

export function elementAction(
  projectId: string,
  scope: SelectionScope,
  action: 'insert' | 'duplicate' | 'delete' | 'move',
  opts: ElementActionOptions,
  editId: string,
): Promise<ElementActionResult> {
  return styleRequest<ElementActionResult>(`/v1/projects/${projectId}/elements`, { scope, action, ...opts, editId });
}

/* ── zip export (binary — bypasses the JSON request() helper) ────────────── */

/** Carries the envelope error code so the UI can tell the paywall
 *  (`payment_required`) apart from ordinary failures. */
export class ExportError extends Error {
  constructor(message: string, readonly code: string) { super(message); }
}

/** Download the project as a zip. Paid-plan feature — the server answers 402
 *  with code `payment_required` for free accounts (admins bypass server-side). */
export async function exportProjectZip(id: string, retried = false): Promise<Blob> {
  if (DEMO_MODE) throw new ExportError('Zip export needs the live API — this is the offline demo.', 'demo');
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}/v1/projects/${id}/export.zip`, {
      headers: { ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}) },
    });
  } catch (err) {
    console.error('[helix-api] network failure on export.zip', err);
    throw new ExportError(CONNECTIVITY_ERROR, 'network');
  }
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return exportProjectZip(id, true);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as Envelope<never> | null;
    throw new ExportError(body?.error?.message ?? `Export failed (${res.status})`, body?.error?.code ?? 'error');
  }
  return res.blob();
}

export type GithubExportResult =
  | { status: 'connect'; url: string }
  | { status: 'exported'; repo: string; repoUrl: string; login: string };

/** Export the project to a GitHub repo in the user's account (paid plans).
 *  'connect' means GitHub isn't linked yet — redirect to `url` (OAuth); the
 *  callback bounces back with ?github=connected for the auto-retry. Throws
 *  ExportError with the envelope code (payment_required / not_configured / …). */
export async function exportToGithub(id: string, retried = false): Promise<GithubExportResult> {
  if (DEMO_MODE) throw new ExportError('GitHub export needs the live API — this is the offline demo.', 'demo');
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}/v1/projects/${id}/export/github`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}),
      },
      body: JSON.stringify({ returnTo: window.location.pathname }),
    });
  } catch (err) {
    console.error('[helix-api] network failure on export/github', err);
    throw new ExportError(CONNECTIVITY_ERROR, 'network');
  }
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return exportToGithub(id, true);
  }
  const body = (await res.json().catch(() => null)) as Envelope<GithubExportResult> | null;
  if (!res.ok || !body?.ok || body.data === undefined) {
    throw new ExportError(body?.error?.message ?? `Export failed (${res.status})`, body?.error?.code ?? 'error');
  }
  return body.data;
}

/** sessionStorage key: the project id whose export is resumed after the OAuth round-trip. */
export const GH_PENDING_KEY = 'helix-gh-pending';

/* ── SSE chat stream ─────────────────────────────────────────────────────── */

let chatVerified = false; // Turnstile is required on the FIRST chat message only (§13)

export async function streamChat(
  projectId: string,
  message: string,
  onEvent: (e: SseEvent) => void,
  requirementsAnswers?: RequirementAnswer[],
  requirementsRevision?: string,
  scope?: SelectionScope,
  forceBuild = false,
  retryBuild = false,
  retried = false,
): Promise<void> {
  if (DEMO_MODE) return demoStream(projectId, message, onEvent, forceBuild); // demo ignores scope
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(`${BASE}/v1/chat`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}),
        ...(chatVerified ? {} : await turnstileHeader('chat')),
      },
      // intake answers ride only on the (first) build that has them; a selected
      // element (scope) rides only when the user is editing one. Conditional-spread
      // keeps the request additive so the SSE contract is unchanged.
      body: JSON.stringify({
        projectId,
        message,
        ...(requirementsAnswers && requirementsAnswers.length ? { requirementsAnswers } : {}),
        ...(requirementsAnswers && requirementsRevision ? { requirementsRevision } : {}),
        ...(scope ? { scope } : {}),
        ...(forceBuild ? { forceBuild: true } : {}),
        ...(retryBuild ? { retryBuild: true } : {}),
      }),
    });
  } catch (err) {
    console.error('[helix-api] network failure on /v1/chat', err);
    throw new Error(CONNECTIVITY_ERROR);
  }
  // 15-min access JWT expired mid-session: rotate it via /v1/auth/refresh and
  // retry the stream once, transparently (same one-shot policy as request()).
  // Only a failed refresh surfaces an error to the UI. The retry MUST forward
  // `scope` — a refresh must not silently drop the user's selection.
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return streamChat(projectId, message, onEvent, requirementsAnswers, requirementsRevision, scope, forceBuild, retryBuild, true);
  }
  if (res.ok) chatVerified = true;
  // 409 = the run mutex rejected this build because one is ALREADY in progress for
  // this project (typically a slow build the user re-sent into). The run executes
  // server-side regardless of this connection, so don't dead-end — rejoin its live
  // feed (replayed from the start via followRun) so the user watches it finish.
  if (res.status === 409) {
    try {
      const st = await api.runStatus(projectId);
      if (st.active && st.runId) {
        onEvent({ type: 'token', text: '\n↻ A build is already running for this project — reconnecting to it…\n' });
        return await followRun(projectId, st.runId, onEvent);
      }
    } catch { /* status unavailable → fall through to the generic notice */ }
    onEvent({ type: 'error', message: 'A build is already running for this project — reload to rejoin it, or try again in a moment.' });
    return;
  }
  // 402 = monthly build cap reached (Stage-1 gate, before any build runs → $0).
  // Re-emit as an 'error' carrying the upgrade message so the UI shows the paywall.
  if (res.status === 402) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    onEvent({ type: 'error', message: body?.error?.message ?? 'Monthly build limit reached — upgrade to keep building.' });
    return;
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    console.error('[helix-api] stream failed', res.status, res.headers.get('content-type'));
    onEvent({
      type: 'error',
      message: body?.error?.message?.trim() || `Stream failed (${res.status}) — the run did not start.`,
    });
    return;
  }
  // Prefer the project's Durable Object WebSocket — events are PUSHED the instant
  // they're produced (no 150ms poll). The runId rides in a response header; an
  // older backend that doesn't send it → just consume the SSE body we already have.
  const runId = res.headers.get('x-run-id');
  if (!runId) { if (res.body) await readSse(res, onEvent); return; }
  void res.body?.cancel().catch(() => { /* drop the server SSE tail; the WS takes over */ });
  await followRun(projectId, runId, onEvent);
}

/**
 * Drive a run's live feed to completion, surviving token expiry AND dropped
 * connections. Tries the DO WebSocket first (instant push), then keeps
 * re-attaching to the SSE tail from the last cursor — attachRun and runStatus
 * each do a one-shot token refresh — until a terminal done/error arrives or the
 * run goes idle. Without this, a stream that drops during a long silent step
 * (e.g. a slow PROVE) leaves a SUCCESSFUL run showing as "stuck on Building".
 */
export async function followRun(
  projectId: string,
  runId: string,
  onEvent: (e: SseEvent) => void,
): Promise<void> {
  if (DEMO_MODE) return; // demo runs are synchronous
  let cursor = 0;
  let terminal = false;
  const stop = new AbortController();
  const track = (e: SseEvent): void => {
    // A late WS/SSE event can arrive after the authoritative D1 result won the
    // race. Never render a second terminal outcome or replay stale progress.
    if (terminal) return;
    // These are transport-layer notices synthesized by the SSE tail, not
    // pipeline outcomes and not entries in the DO event buffer. Do not advance
    // the durable cursor or show them as failed builds; reconcile through D1.
    if (e.type === 'error' && (
      e.message === 'Lost contact with the run — check the project history.'
      || e.message === 'Run timed out.'
    )) return;
    cursor += 1;
    if (e.type === 'done' || e.type === 'error') {
      terminal = true;
      stop.abort();
    }
    onEvent(e);
  };
  // Refresh a stale token BEFORE the WS handshake (which can't 401-and-retry), so
  // the socket connects on the first try (instant push) rather than failing auth
  // and dropping to the SSE fallback. Matters most on reload-rejoin (no preceding POST).
  const refreshBeforeSocket = localStorage.getItem(REFRESH_KEY);
  if (accessExpiresSoon() && refreshBeforeSocket) {
    try { await tryRefresh(refreshBeforeSocket); } catch { /* SSE fallback will refresh if this didn't */ }
  }
  const deadline = Date.now() + 21 * 60_000;
  // Delivery and reconciliation deliberately run IN PARALLEL. A WebSocket can
  // remain healthy/open while its terminal frame is lost during a deployment;
  // awaiting that socket before consulting D1 left the Builder on “Working”
  // until a hard reload. The persisted assistant row is the terminal truth.
  const delivery = (async (): Promise<void> => {
    try { await streamRunWS(projectId, runId, track, stop.signal); } catch { /* rejoin below */ }
    while (!terminal && Date.now() < deadline) {
      try { await attachRun(projectId, runId, track, cursor, false, stop.signal); } catch { /* transient */ }
      if (!terminal) await sleep(1000, stop.signal);
    }
  })();
  const reconciliation = (async (): Promise<void> => {
    while (!terminal && Date.now() < deadline) {
      try {
        const outcome = await api.runResult(projectId, runId);
        if (outcome.state === 'succeeded') {
          track({ type: 'done', runId });
          break;
        }
        if (outcome.state === 'failed') {
          track({ type: 'error', message: outcome.message ?? 'Run failed' });
          break;
        }
      } catch { /* rolling deploy / transient — keep polling while delivery stays live */ }
      await sleep(1250, stop.signal);
    }
  })();
  await Promise.allSettled([delivery, reconciliation]);
  stop.abort();
  // Never leave the UI on "Building": if no terminal event ever reached us
  // (persistent connectivity loss / run vanished), surface a neutral notice — a
  // reload reconciles with server truth (the build may well have succeeded).
  if (!terminal) onEvent({ type: 'error', message: 'Lost the live connection to this run — reload to see the latest.' });
}

/** Live run delivery over the DO WebSocket: instant push, no polling. Falls back
 *  to the SSE rejoin tail — resuming at the exact cursor already received, so no
 *  duplicate tokens — if the socket can't connect or drops before `done`. */
async function streamRunWS(
  projectId: string,
  runId: string,
  onEvent: (e: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const wsBase = BASE.replace(/^http/, 'ws'); // https→wss, http→ws
  const url = `${wsBase}/v1/projects/${projectId}/ws`
    + `?runId=${encodeURIComponent(runId)}&since=0&token=${encodeURIComponent(getAccess() ?? '')}`;
  return new Promise<void>((resolve) => {
    let settled = false;
    let received = 0; // events applied → the SSE-fallback resume cursor
    let ws: WebSocket | null = null;
    const aborted = (): void => finish();
    const toSse = (): void => {
      if (settled) return;
      if (signal?.aborted) { finish(); return; }
      settled = true;
      signal?.removeEventListener('abort', aborted);
      try { ws?.close(); } catch { /* already closing */ }
      void attachRun(projectId, runId, onEvent, received, false, signal).then(resolve, () => resolve());
    };
    function finish(): void {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', aborted);
      try { ws?.close(); } catch { /* */ }
      resolve();
    }
    try { ws = new WebSocket(url); } catch { toSse(); return; }
    signal?.addEventListener('abort', aborted, { once: true });
    if (signal?.aborted) { finish(); return; }
    const openTimer = setTimeout(() => { if (ws?.readyState !== WebSocket.OPEN) toSse(); }, 5000);

    ws.onopen = (): void => clearTimeout(openTimer);
    ws.onmessage = (ev: MessageEvent): void => {
      let evt: unknown;
      try { evt = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); } catch { return; /* keep-alive */ }
      if (!isSseEvent(evt)) return;
      received++;
      onEvent(evt);
      if (evt.type === 'done' || evt.type === 'error') finish();
    };
    ws.onerror = (): void => { clearTimeout(openTimer); toSse(); };
    ws.onclose = (): void => { clearTimeout(openTimer); toSse(); }; // no-op if already settled
  });
}

/** Read a `data: {json}\n` SSE body, forwarding each valid AgentEvent. */
async function readSse(res: Response, onEvent: (e: SseEvent) => void): Promise<void> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const evt: unknown = JSON.parse(line.slice(6));
        if (isSseEvent(evt)) onEvent(evt);
      } catch { /* keep-alive */ }
    }
  }
}

/** Rejoin a run still executing server-side after a reload: GET the run-stream
 *  tail (full replay from the start, then live to done). Mirrors streamChat's
 *  auth + one-shot refresh. */
export async function attachRun(
  projectId: string,
  runId: string,
  onEvent: (e: SseEvent) => void,
  since = 0,
  retried = false,
  signal?: AbortSignal,
): Promise<void> {
  if (DEMO_MODE) return; // demo runs are synchronous — nothing to rejoin
  const requestAccess = getAccess();
  const requestRefresh = localStorage.getItem(REFRESH_KEY);
  let res: Response;
  try {
    res = await fetch(
      `${BASE}/v1/projects/${projectId}/run-stream?runId=${encodeURIComponent(runId)}&since=${since}`,
      {
        headers: { ...(requestAccess ? { authorization: `Bearer ${requestAccess}` } : {}) },
        signal,
      },
    );
  } catch (err) {
    console.error('[helix-api] network failure on run-stream', err);
    throw new Error(CONNECTIVITY_ERROR);
  }
  if (res.status === 401 && !retried && requestRefresh) {
    if (await tryRefresh(requestRefresh)) return attachRun(projectId, runId, onEvent, since, true, signal);
  }
  // A non-ok / bodyless response is a CONNECTION failure, not a run failure —
  // THROW so followRun recovers (re-check runStatus, re-attach) instead of
  // mistaking a transient 5xx/52x mid-run for a failed build. (streamRunWS's
  // internal call resolves on rejection; followRun's loop catches and retries.)
  if (!res.ok || !res.body) throw new Error(`run-stream unavailable (${res.status})`);
  await readSse(res, onEvent);
}

/* ── §11.3 patch-stream interceptor ─────────────────────────────────────── */

export interface PatchBlock { path: string; search: string; replace: string }

const MARKER_OPEN = '<<<<<<< '; // shared prefix of both the SEARCH and NEW openers
// Tolerate a stray `>` after the closing quote (`path="…">`) — a common model
// malformation that otherwise makes the opener read as prose and leaks the whole
// raw block (code + image URLs) into the chat.
const SEARCH_RE = /^<{7} SEARCH path="([^"]*)"\s*>?\s*$/;
const NEW_RE = /^<{7} NEW path="([^"]*)"\s*>?\s*$/;
const DIVIDER_RE = /^={7}\s*$/;
// One closer for either kind: the model sometimes swaps REPLACE/END, and matching
// either keeps a NEW block from swallowing the rest of the stream forever.
const CLOSER_RE = /^>{7} (?:REPLACE|END)\s*$/;

/**
 * Incremental filter for §11.3 SEARCH/REPLACE blocks inside a `token` stream.
 * Prose is forwarded chunk-by-chunk through onText — a partial line is held
 * back only while it could still grow into a SEARCH marker — and completed
 * blocks arrive via onPatch. Marker lines themselves never reach onText.
 */
export function createPatchInterceptor(
  onText: (text: string) => void,
  onPatch: (patch: PatchBlock) => void,
): { push: (chunk: string) => void; flush: () => string | null } {
  let buf = '';
  let midLine = false; // earlier part of the current line already forwarded
  let mode: 'text' | 'search' | 'replace' | 'new' = 'text';
  let path = '';
  let search: string[] = [];
  let replace: string[] = [];

  const takeLine = (line: string): void => {
    if (mode === 'text') {
      const open = SEARCH_RE.exec(line);
      if (open) { mode = 'search'; path = open[1] ?? ''; search = []; replace = []; return; }
      const fresh = NEW_RE.exec(line);
      if (fresh) { mode = 'new'; path = fresh[1] ?? ''; search = []; replace = []; return; }
      onText(line + '\n');
    } else if (mode === 'search') {
      if (DIVIDER_RE.test(line)) mode = 'replace';
      else search.push(line);
    } else if (mode === 'new') {
      // whole-file create: swallow content until the closer, then emit as a patch with empty search
      if (CLOSER_RE.test(line)) { onPatch({ path, search: '', replace: replace.join('\n') }); mode = 'text'; }
      else replace.push(line);
    } else if (CLOSER_RE.test(line)) {
      onPatch({ path, search: search.join('\n'), replace: replace.join('\n') });
      mode = 'text';
    } else {
      replace.push(line);
    }
  };

  return {
    push(chunk: string): void {
      buf += chunk;
      for (let nl = buf.indexOf('\n'); nl !== -1; nl = buf.indexOf('\n')) {
        const line = buf.slice(0, nl);
        buf = buf.slice(nl + 1);
        if (midLine) { onText(line + '\n'); midLine = false; } // mid-line: can't be a marker
        else takeLine(line);
      }
      if (mode === 'text' && buf &&
          (midLine || !(MARKER_OPEN.startsWith(buf) || buf.startsWith(MARKER_OPEN)))) {
        onText(buf);
        buf = '';
        midLine = true;
      }
    },
    /** End of stream. Returns the path of a block left unterminated (its
     *  content was withheld from display and never applied), or null. */
    flush(): string | null {
      const truncated = mode === 'text' ? null : path;
      if (mode === 'text' && buf) onText(buf);
      buf = '';
      midLine = false;
      mode = 'text';
      return truncated;
    },
  };
}

/** One-shot form for persisted history: strip §11.3 blocks so a reloaded
 *  conversation shows the same text the live stream rendered. */
export function stripPatchBlocks(text: string): string {
  let out = '';
  const filter = createPatchInterceptor((t) => { out += t; }, () => { /* display only */ });
  filter.push(text);
  filter.flush();
  return out.replace(/\n+$/, '');
}

/** Whether the paid-billing UI (Checkout, upgrade buttons, plan auto-start) is live.
 *  Build-time gate so the new pricing can ship to prod as DISPLAY-ONLY — paid plans
 *  show as "launching soon" — until Stripe is configured on the API worker. Build
 *  with VITE_BILLING_LIVE=1 to turn the real purchase flow on. */
export const BILLING_LIVE = import.meta.env.VITE_BILLING_LIVE === '1';

/** Backend `error` events for hitting the monthly build cap (402 / Stage-2) get the
 *  upgrade paywall instead of a generic error row. */
export function isCreditCapError(message: string): boolean {
  return /build limit|monthly.{0,14}limit|upgrade to keep building|reached your monthly/i.test(message);
}

/* ═════════════ Offline demo store (zero-backend product demo) ═══════════ */

const demoDb = {
  user: { id: 'demo', email: 'demo@helix.app', plan: 'free' } as User,
  projects: [] as Project[],
  files: new Map<string, Map<string, string>>(),
  commits: new Map<string, Commit[]>(),
  publications: new Map<string, { slug: string; url: string }>(),
  appUsers: new Map<string, Map<string, GeneratedAppUser & { password: string }>>(),
  appSessions: new Map<string, string>(),
  environment: new Map<string, ProjectEnvironmentVariable[]>(),
  commerce: new Map<string, CommerceStatus>(),
};

function seedFiles(): Map<string, string> {
  return new Map<string, string>([
    ['index.html', `<!doctype html>\n<html>\n<head>\n  <meta charset="utf-8" />\n  <meta name="viewport" content="width=device-width, initial-scale=1" />\n  <title>New Project</title>\n  <link rel="stylesheet" href="styles.css" />\n</head>\n<body>\n  <main><!-- helix:insert --></main>\n  <script src="app.js"></script>\n</body>\n</html>\n`],
    ['styles.css', `html,body,main{margin:0;min-height:100%;background:#fff}\n`],
    ['app.js', `// Start building with Piixel.\n`],
  ]);
}

function blankSeedFiles(): Map<string, string> {
  return new Map<string, string>([
    ['index.html', `<!doctype html>\n<html>\n<head>\n  <meta charset="utf-8" />\n  <meta name="viewport" content="width=device-width, initial-scale=1" />\n  <title>Untitled Project</title>\n  <link rel="stylesheet" href="styles.css" />\n</head>\n<body>\n  <main><!-- helix:insert --></main>\n  <script src="app.js"></script>\n</body>\n</html>\n`],
    ['styles.css', `html,body,main{margin:0;min-height:100%;background:#fff}\n`],
    ['app.js', `// Start building with Piixel.\n`],
  ]);
}

/** Offline intake set — mirrors helix-api's fallbackQuestionnaire() so the demo
 *  funnel + morph are fully exercisable with no backend. */
const DEMO_INTAKE: IntakeQuestionnaire = {
  title: 'A few quick questions',
  questions: [
    { id: 'audience', label: 'Who is this mainly for?', type: 'radio', options: [
      { label: 'Anyone (public)', value: 'public' }, { label: 'Signed-in members', value: 'members' }, { label: 'Just me', value: 'personal' } ] },
    { id: 'goal', label: 'What should visitors mainly do?', type: 'pills', options: [
      { label: 'Learn about it', value: 'learn' }, { label: 'Sign up', value: 'signup' }, { label: 'Browse products', value: 'browse' }, { label: 'Book or contact', value: 'book' } ] },
    { id: 'tone', label: 'Pick a visual vibe', type: 'pills', options: [
      { label: 'Minimal', value: 'minimal' }, { label: 'Bold', value: 'bold' }, { label: 'Premium', value: 'premium' }, { label: 'Playful', value: 'playful' } ] },
    { id: 'detail', label: 'Anything specific I should include? (optional)', type: 'text' },
  ],
};

function demoIntake(prompt: string): IntakeQuestionnaire {
  if (isDemoDiscussion(prompt)) {
    return { title: prompt, questions: [], commerce: false, mode: 'discuss' };
  }
  if (!/(?:\b(?:shop|store|checkout|cart|sell|e-?commerce|webbutik|webbshop|butik|kassa|varukorg|sälja|foroshgah|kharid|pardakht)\b|فروشگاه|خرید|پرداخت|متجر|شراء|购物|商店)/iu.test(prompt)) return DEMO_INTAKE;
  return {
    ...DEMO_INTAKE,
    commerce: true,
    questions: [
      ...DEMO_INTAKE.questions.slice(0, 3),
      { id: 'commerce-checkout', label: 'Should customers be able to pay securely on the site?', type: 'radio', required: true, options: [
        { label: 'Yes, with Stripe', value: 'stripe-checkout' },
        { label: 'No, browsing/contact only', value: 'catalog-only' },
      ] },
    ],
  };
}

async function demoRequest<T>(path: string, init: RequestInit): Promise<T> {
  await new Promise((r) => setTimeout(r, 120));
  const method = init.method ?? 'GET';
  const ret = (v: unknown) => v as T;

  if (path === '/v1/auth/signup' && method === 'POST') {
    const body = JSON.parse(String(init.body ?? '{}')) as { email?: string };
    demoDb.user = { ...demoDb.user, email: body.email ?? demoDb.user.email };
    return ret({
      verificationRequired: true,
      email: demoDb.user.email,
      expiresInSeconds: 900,
      resendAfterSeconds: 1,
    });
  }
  if (path === '/v1/auth/signup/verify' && method === 'POST') {
    setTokens('demo-access', 'demo-refresh');
    return ret({ user: demoDb.user, access: 'demo-access', refresh: 'demo-refresh' });
  }
  if (path.startsWith('/v1/auth/')) {
    if (method === 'POST') {
      const body = JSON.parse(String(init.body ?? '{}')) as { email?: string };
      demoDb.user = { ...demoDb.user, email: body.email ?? demoDb.user.email };
      setTokens('demo-access', 'demo-refresh');
      return ret({ user: demoDb.user, access: 'demo-access', refresh: 'demo-refresh' });
    }
    return ret({ user: demoDb.user });
  }
  if (path.endsWith('/run-status')) return ret({ active: false, runId: null }); // demo runs are synchronous
  if (/\/intake$/.test(path) && method === 'POST') {
    const body = JSON.parse(String(init.body ?? '{}')) as { prompt?: string };
    return ret(demoIntake(body.prompt ?? ''));
  }
  const commerceMatch = path.match(/^\/v1\/commerce\/projects\/([^/]+)\/(status|connect|connection|checkout)$/);
  if (commerceMatch) {
    const projectId = commerceMatch[1] ?? '';
    const action = commerceMatch[2];
    const current = demoDb.commerce.get(projectId) ?? {
      configured: false, webhookConfigured: false, connected: false, chargesEnabled: false, payoutsEnabled: false,
      detailsSubmitted: false, authorizationStatus: 'deauthorized', stripeRestrictionStatus: 'unknown',
      riskStatus: 'unknown', publishedCheckoutStatus: 'paused', platformStatus: 'active',
      catalog: { valid: false, products: 0, draftProducts: 0 }, ready: false,
    };
    if (action === 'status') return ret(current);
    if (action === 'connect') return ret({ url: '#' });
    if (action === 'connection') { demoDb.commerce.delete(projectId); return ret({ disconnected: true }); }
    return ret({ url: '#' });
  }
  if (/^\/v1\/commerce\/projects\/[^/]+\/catalog\/reconcile$/.test(path) && method === 'POST') {
    return ret({ changed: false, files: [] });
  }
  if (/^\/v1\/commerce\/projects\/[^/]+\/orders(?:\/[^/]+\/fulfillment)?$/.test(path)) {
    return ret(method === 'GET' ? { orders: [], totals: [] } : { id: 'demo-order', fulfillmentStatus: 'fulfilled' });
  }
  if (path === '/v1/admin/commerce/projects') return ret({ projects: [] });
  const commitsMatch = path.match(/^\/v1\/projects\/([^/]+)\/commits$/);
  if (commitsMatch) return ret({ commits: demoDb.commits.get(commitsMatch[1] ?? '') ?? [] });
  if (path === '/v1/credits') return ret({ remaining: 30, total: 30, resetsAt: '', plan: 'free' });
  if (path === '/v1/projects' && method === 'GET') return ret({ projects: demoDb.projects });
  if (path === '/v1/projects/preproject' && method === 'POST') {
    const body = JSON.parse(String(init.body ?? '{}')) as { message?: string };
    const message = body.message?.trim() ?? '';
    if (!message) throw new Error('Write a message or describe the app you want to build.');
    if (isDemoDiscussion(message)) {
      throw new Error('AI planning conversations need the connected Piixel API and are unavailable in offline demo mode.');
    }
    return ret({ mode: 'build', prompt: message, titlePrompt: message });
  }
  if (path === '/v1/projects' && method === 'POST') {
    const body = JSON.parse(String(init.body)) as { name?: string; prompt?: string; blank?: boolean };
    const name = body.name?.trim()
      || (body.prompt ?? (body.blank ? 'Untitled Project' : 'New Project')).split(/\s+/).slice(0, 3).map((w) => (w[0] ?? '').toUpperCase() + w.slice(1)).join(' ').slice(0, 30)
      || (body.blank ? 'Untitled Project' : 'New Project');
    const p: Project = {
      id: `p_${Date.now()}`, name, slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      template: body.blank ? 'blank' : 'web', created_at: new Date().toISOString(),
    };
    demoDb.projects.unshift(p);
    demoDb.files.set(p.id, body.blank ? blankSeedFiles() : seedFiles());
    demoDb.commits.set(p.id, []);
    return ret({ project: p });
  }
  const appAuthMatch = path.match(
    /^\/v1\/projects\/([^/]+)\/app-auth\/(register|login|logout|me|request-email-verification|verify-email|forgot-password|reset-password)$/,
  );
  if (appAuthMatch) {
    const projectId = appAuthMatch[1] ?? '';
    const action = appAuthMatch[2] as GeneratedAppAuthAction;
    const users = demoDb.appUsers.get(projectId) ?? new Map<string, GeneratedAppUser & { password: string }>();
    demoDb.appUsers.set(projectId, users);
    if (action === 'me') {
      const userId = demoDb.appSessions.get(projectId);
      const user = [...users.values()].find((candidate) => candidate.id === userId);
      return ret({ user: user ? { id: user.id, name: user.name, email: user.email, emailVerified: user.emailVerified } : null });
    }
    if (action === 'logout') {
      demoDb.appSessions.delete(projectId);
      return ret({ user: null });
    }
    const body = JSON.parse(String(init.body ?? '{}')) as { name?: string; email?: string; password?: string; code?: string };
    const email = body.email?.trim().toLowerCase() ?? '';
    if (action === 'request-email-verification') {
      const userId = demoDb.appSessions.get(projectId);
      const user = [...users.values()].find((candidate) => candidate.id === userId);
      if (!user) throw new Error('Sign in to verify your email');
      return ret({ user, verificationRequired: !user.emailVerified, verificationSent: !user.emailVerified });
    }
    if (action === 'verify-email') {
      const userId = demoDb.appSessions.get(projectId);
      const user = [...users.values()].find((candidate) => candidate.id === userId);
      if (!user) throw new Error('Sign in to verify your email');
      user.emailVerified = true;
      return ret({ user, verificationRequired: false });
    }
    if (action === 'forgot-password') {
      return ret({ user: null, accepted: true, message: 'If an account exists, a password reset code has been sent.' });
    }
    if (action === 'reset-password') {
      const user = users.get(email);
      if (!user || !body.code || !body.password) throw new Error('That password reset code is invalid or expired');
      user.password = body.password;
      demoDb.appSessions.delete(projectId);
      return ret({ user: null, passwordReset: true, message: 'Your password has been reset. Sign in with your new password.' });
    }
    if (action === 'register') {
      if (users.has(email)) throw new Error('An account with this email already exists');
      const user = {
        id: `app-user-${Date.now()}`,
        name: body.name?.trim() || 'Member',
        email,
        emailVerified: false,
        password: body.password ?? '',
      };
      users.set(email, user);
      demoDb.appSessions.set(projectId, user.id);
      return ret({ user, verificationRequired: true, verificationSent: true });
    }
    const user = users.get(email);
    if (!user || user.password !== body.password) throw new Error('Email or password is incorrect');
    demoDb.appSessions.set(projectId, user.id);
    return ret({ user, verificationRequired: !user.emailVerified });
  }
  const environmentMatch = path.match(/^\/v1\/projects\/([^/]+)\/environment(?:\/(preview|production)\/([^/]+))?$/);
  if (environmentMatch) {
    const projectId = environmentMatch[1] ?? '';
    const variables = demoDb.environment.get(projectId) ?? [];
    if (method === 'GET') return ret({ variables });
    const environment = environmentMatch[2] as ProjectEnvironment;
    const name = decodeURIComponent(environmentMatch[3] ?? '');
    if (method === 'DELETE') {
      demoDb.environment.set(projectId, variables.filter((item) => item.environment !== environment || item.name !== name));
      return ret({ deleted: true });
    }
    const body = JSON.parse(String(init.body ?? '{}')) as { kind: ProjectEnvironmentKind; value: string };
    const variable: ProjectEnvironmentVariable = {
      environment,
      name,
      kind: body.kind,
      ...(body.kind === 'public' ? { value: body.value } : {}),
      configured: true,
      updated_at: new Date().toISOString(),
    };
    demoDb.environment.set(projectId, [
      ...variables.filter((item) => item.environment !== environment || item.name !== name),
      variable,
    ]);
    return ret({ variable });
  }
  const projMatch = path.match(/^\/v1\/projects\/([^/?]+)$/);
  if (projMatch && method === 'PATCH') {
    const { name } = JSON.parse(String(init.body)) as { name: string };
    const p = demoDb.projects.find((x) => x.id === projMatch[1]);
    if (p) p.name = name;
    return ret({ project: p ?? demoDb.projects[0] });
  }
  if (projMatch && method === 'GET') {
    return ret({ project: demoDb.projects.find((x) => x.id === projMatch[1]) ?? demoDb.projects[0] });
  }
  if (projMatch && method === 'DELETE') {
    demoDb.projects = demoDb.projects.filter((x) => x.id !== projMatch[1]);
    demoDb.files.delete(projMatch[1] ?? '');
    demoDb.commits.delete(projMatch[1] ?? '');
    demoDb.appUsers.delete(projMatch[1] ?? '');
    demoDb.appSessions.delete(projMatch[1] ?? '');
    demoDb.environment.delete(projMatch[1] ?? '');
    return ret({});
  }
  const filesMatch = path.match(/^\/v1\/projects\/([^/?]+)\/files(?:\?content=1)?$/);
  if (filesMatch && method === 'PUT') {
    const { path: fp, content } = JSON.parse(String(init.body)) as { path: string; content: string };
    let f = demoDb.files.get(filesMatch[1] ?? '');
    if (!f) { f = seedFiles(); demoDb.files.set(filesMatch[1] ?? '', f); }
    f.set(fp, content);
    return ret({ file: { path: fp, size: content.length, updated_at: new Date().toISOString() } });
  }
  if (filesMatch) {
    const f = demoDb.files.get(filesMatch[1] ?? '') ?? seedFiles();
    const wantsContent = path.endsWith('?content=1');
    return ret({
      files: [...f.entries()].map(([p, c]) => ({
        path: p,
        size: c.length,
        updated_at: new Date().toISOString(),
        ...(wantsContent ? { content: c } : {}),
      })),
    });
  }
  const contentMatch = path.match(/^\/v1\/projects\/([^/]+)\/files\/content\?path=(.+)$/);
  if (contentMatch) {
    const f = demoDb.files.get(contentMatch[1] ?? '');
    const p = decodeURIComponent(contentMatch[2] ?? '');
    return ret({ path: p, content: f?.get(p) ?? '' });
  }
  if (/\/messages$/.test(path)) return ret({ messages: [] });
  if (/\/snapshots$/.test(path)) return ret({ snapshots: [] });
  if (path === '/v1/usage') return ret({ period: 'demo', tokens_in: 4210, tokens_out: 9818, billed_usd: 0 });
  const pubMatch = path.match(/^\/v1\/projects\/([^/]+)\/publish$/);
  if (pubMatch) {
    const pid = pubMatch[1] ?? '';
    if (method === 'POST') {
      const rec = { slug: 'demo-app', url: 'https://piixel.ai/s/demo-app' };
      demoDb.publications.set(pid, rec);
      return ret({ published: true, ...rec });
    }
    if (method === 'DELETE') { demoDb.publications.delete(pid); return ret({ unpublished: true }); }
    const rec = demoDb.publications.get(pid);
    return ret(rec ? { published: true, ...rec } : { published: false });
  }
  const domainMatch = path.match(/^\/v1\/projects\/([^/]+)\/domain(?:\?.*)?$/);
  if (domainMatch) {
    if (method === 'DELETE') return ret({ removed: true });
    return ret({ available: false, configured: false, domain: null });
  }
  // admin panel (no backend in demo): empty account list + no-op destructive ops
  if (path === '/v1/auth/presence' && method === 'POST') return ret({ online: true });
  if (path === '/v1/admin/users') return ret({ users: [], count: 0 });
  if (path === '/v1/admin/me/plan' && method === 'PUT') {
    const requested = String((JSON.parse(String(init.body ?? '{}')) as { plan?: string }).plan ?? 'free');
    const plan = requested === 'business' ? 'team' : requested;
    return ret({ plan, selectedPlan: requested });
  }
  if (/^\/v1\/admin\/users\/[^/]+\/projects$/.test(path) && method === 'DELETE') {
    return ret({ operationId: 'demo-purge', status: 'complete', queued: 0, total: 0, remaining: 0 });
  }
  if (/^\/v1\/admin\/users\/[^/]+\/projects\/purge-status(?:\?.*)?$/.test(path) && method === 'POST') {
    return ret({ operationId: 'demo-purge', status: 'complete', remaining: 0 });
  }
  if (/^\/v1\/admin\/users\/[^/]+$/.test(path) && method === 'DELETE') {
    return ret({ deleted: true, email: 'demo@helix.app', projectsQueued: 0, projectsTotal: 0 });
  }
  // Billing has no offline path — throw instead of the {restored:true} catch-all so a
  // checkout/portal call in demo mode surfaces a clear message, never a silent no-op.
  if (path.startsWith('/v1/billing/')) throw new Error('Billing isn’t available in offline demo mode.');
  return ret({ restored: true });
}

async function demoStream(projectId: string, message: string, onEvent: (e: SseEvent) => void, forceBuild = false): Promise<void> {
  const say = async (e: SseEvent, ms = 40) => { onEvent(e); await new Promise((r) => setTimeout(r, ms)); };
  if (!forceBuild && isDemoDiscussion(message)) {
    const files = demoDb.files.get(projectId);
    const paths = files ? [...files.keys()] : [];
    const stack = files?.has('src/main.tsx') ? 'a Vite, React and TypeScript app' : 'a web app';
    const answer = `I inspected the current project without changing it. It is ${stack} with ${paths.length} files` +
      `${paths.length ? `, including ${paths.slice(0, 8).join(', ')}` : ''}. In offline demo mode I can discuss its structure; connect the API for a deeper answer to this specific question.`;
    for (const text of answer.split(/(?<= )/)) await say({ type: 'token', text }, 18);
    await say({ type: 'usage', tokens_in: 0, tokens_out: 0, billed: false }, 40);
    await say({ type: 'done', runId: `run_${Date.now()}` });
    return;
  }
  await say({ type: 'phase', name: 'plan' }, 300);
  for (const t of `1. Read index.html\n2. Apply your request: "${message.slice(0, 64)}"\n3. Validate + snapshot\n`.split(/(?<= )/)) {
    await say({ type: 'token', text: t }, 24);
  }
  await say({ type: 'phase', name: 'patch' }, 350);
  const files = demoDb.files.get(projectId);
  if (files) {
    const html = files.get('index.html') ?? '';
    const base = forceBuild
      ? html.replace(
        '<!-- helix:insert -->',
        `<section style="padding:64px;font-family:system-ui"><h1>${escapeHtml(message.slice(0, 80))}</h1><p>Your new project is ready to refine.</p></section>`,
      )
      : html;
    files.set('index.html', base.replace(
      '<p>Describe a change in the chat and watch this app evolve.</p>',
      `<p>Describe a change in the chat and watch this app evolve.</p>\n    <p style="color:#0891b2">✦ ${escapeHtml(message.slice(0, 80))}</p>`,
    ));
  }
  await say({ type: 'file', path: 'index.html', action: 'patch' }, 250);
  await say({ type: 'phase', name: 'prove' }, 400);
  for (const t of 'tsc clean · 0 lint errors · build ok. Patched index.html with your request — open the Preview tab. '.split(/(?<= )/)) {
    await say({ type: 'token', text: t }, 18);
  }
  await say({ type: 'usage', tokens_in: 412, tokens_out: 188, billed: false }, 60);
  const runId = `run_${Date.now()}`;
  const commits = demoDb.commits.get(projectId) ?? [];
  demoDb.commits.set(projectId, [{
    run_id: runId,
    tag: `run/${runId}`,
    summary: message.slice(0, 120),
    file_count: demoDb.files.get(projectId)?.size ?? 0,
    created_at: new Date().toISOString(),
  }, ...commits]);
  await say({ type: 'done', runId });
}

function isDemoDiscussion(message: string): boolean {
  const text = message.trim();
  if (/^(?:(?:hello there|hey there|good morning|good afternoon|good evening|hello|hi|hey|hej|hallå|tjena|god morgon)(?:[\s,]+(?:piixel|pixel))?)[\s,.!?]*$/iu.test(text)) return true;
  if (/\b(?:and|then)\s+(?:add|change|update|remove|delete|fix|implement|build|create|make|apply)\b/i.test(text)) return false;
  if (/^(?:please\s+)?(?:add|change|update|remove|delete|fix|implement|build|create|make|apply)\b/i.test(text)) return false;
  if (/^(?:can|could|would)\s+(?:you|we)\s+(?:add|change|update|remove|delete|fix|implement|build|create|make|apply)\b/i.test(text)) return false;
  return /^(?:what|why|how|which|where|when|who|is|are|does|do|did|should|tell me|explain|analy[sz]e|review|audit|summari[sz]e|describe|compare|suggest|recommend)\b/i.test(text)
    || text.endsWith('?');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}
